import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { QA_NAME_PROFILE_INFO_ITEM_VALUE } from '../../../constants/qa'
import { useOutboundUuid, useUserData } from '../../../hooks'
import { isWorkerUser } from '../../../utils/user'
import { AvatarWithStatus } from '../../Avatar'
import {
  EntityProfileBuildingItem,
  EntityProfileFlatItem,
  EntityProfileHeaderInfoItemBase,
  EntityProfileHeaderInfoItemDeadline,
  EntityProfileHeaderInfoItemText,
} from '../../Entity'
import Text from '../../Text'
import { ProfileUserInfoItem } from '../../Widget'
import { useRequestProfileContext } from '../RequestProfile.hooks'
import { RequestProfileHeaderOwnerIcons } from '../RequestProfileHeaderOwnerIcons'

import {
  REQUEST_PROFILE_HEADER_INFO_ITEM_EMPTY_TEXT_KEYS,
  REQUEST_PROFILE_HEADER_INFO_ITEM_TITLE_KEYS,
  RequestProfileHeaderInfoItemName,
} from './RequestProfileHeaderInfoItem.constants'
import {
  getRequestProfileHeaderInfoItemBaseProps,
  isRequestProfileHeaderInfoItemDeadline,
  isRequestProfileHeaderOwnerNeedToShowIcons,
  isRequestProfileHeaderOwnerVisibilityIconVisible,
} from './RequestProfileHeaderInfoItem.utils'

import * as styles from './RequestProfileHeaderInfoItem.module.css'

function RequestProfileHeaderInfoItem({
  canEdit,
  name,
  requestData,
  ...restProps
}) {
  const { t } = useTranslation()

  const outboundUuid = useOutboundUuid()

  const userData = useUserData()

  const { handleUpdateEntity } = useRequestProfileContext()

  const isWorker = !!userData && isWorkerUser(userData)

  const isConnectedRequest = Boolean(outboundUuid)

  const isDeadlineItem = isRequestProfileHeaderInfoItemDeadline(name)

  const itemBaseProps = getRequestProfileHeaderInfoItemBaseProps({
    canEdit,
    contentWrapperClassName: classNames(styles.contentWrapper, {
      [styles.deadlineEditableContentWrapper]: canEdit && isDeadlineItem,
      [styles.editableContentWrapper]: canEdit && !isDeadlineItem,
    }),
    editFieldsWrapperClassName: isDeadlineItem
      ? styles.deadlineEditFieldsWrapper
      : undefined,
    itemActionsWrapperClassName: styles.itemActions,
    name,
    noDataTextClassName:
      isDeadlineItem && canEdit ? styles.deadlineNoDataText : undefined,
    titleClassName: styles.itemTitle,
  })

  const noDataTextKey =
    REQUEST_PROFILE_HEADER_INFO_ITEM_EMPTY_TEXT_KEYS[name] || 'Select.NoDataKey'

  const flatText = requestData?.flat?.number
    ? `${t('Common.FlatSelectListItemNumberPrefixKey')} ${requestData.flat.number}`
    : null

  const isOwnerVisibilityIconVisible =
    isRequestProfileHeaderOwnerVisibilityIconVisible(requestData)

  function handleOwnerNeedToShowIcons({ isArchived, isHovered }) {
    return isRequestProfileHeaderOwnerNeedToShowIcons({
      isArchived,
      isHovered,
      isOwnerVisibilityIconVisible,
    })
  }

  async function handleSaveDueDate(nextValue) {
    return handleUpdateEntity({ due_date: nextValue })
  }

  switch (name) {
    case RequestProfileHeaderInfoItemName.Deadline:
      return (
        <EntityProfileHeaderInfoItemDeadline
          {...restProps}
          {...itemBaseProps}
          noDataTextKey={noDataTextKey}
          status={requestData?.status?.id}
          title={REQUEST_PROFILE_HEADER_INFO_ITEM_TITLE_KEYS[name]}
          value={requestData?.due_date}
          onSaveValue={handleSaveDueDate}
        />
      )

    case RequestProfileHeaderInfoItemName.Owner:
      return (
        <EntityProfileHeaderInfoItemBase
          {...restProps}
          {...itemBaseProps}
          contentExist={!!requestData?.owner}
          title={REQUEST_PROFILE_HEADER_INFO_ITEM_TITLE_KEYS[name]}
          value={requestData?.owner?.id}
        >
          {requestData?.owner ? (
            <ProfileUserInfoItem
              AdditionalInfo={null}
              ContentAvatar={AvatarWithStatus}
              Icons={RequestProfileHeaderOwnerIcons}
              blocked={isWorker}
              className={styles.ownerItem}
              hoverClassName={null}
              iconsProps={{
                showVisibilityIcon: isOwnerVisibilityIconVisible,
              }}
              isArchived={Boolean(requestData?.owner?.archived)}
              isNeedToShowIcons={handleOwnerNeedToShowIcons}
              textQaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}
              value={requestData.owner}
            />
          ) : (
            <Text disabled isSecondary qaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}>
              {t(noDataTextKey)}
            </Text>
          )}
        </EntityProfileHeaderInfoItemBase>
      )

    case RequestProfileHeaderInfoItemName.Category:
      return (
        <EntityProfileHeaderInfoItemText
          {...restProps}
          {...itemBaseProps}
          noDataText={noDataTextKey}
          title={REQUEST_PROFILE_HEADER_INFO_ITEM_TITLE_KEYS[name]}
          value={requestData?.category?.name}
        />
      )

    case RequestProfileHeaderInfoItemName.Building:
      return (
        <EntityProfileHeaderInfoItemBase
          {...restProps}
          {...itemBaseProps}
          contentExist={!!requestData?.building?.id}
          title={REQUEST_PROFILE_HEADER_INFO_ITEM_TITLE_KEYS[name]}
          value={requestData?.building?.id}
        >
          {requestData?.building ? (
            <EntityProfileBuildingItem
              blocked={isWorker || isConnectedRequest}
              className={styles.buildingItem}
              hoverClassName={null}
              isArchived={requestData?.building?.archived}
              text={requestData?.building?.address?.value}
              textQaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}
              value={requestData?.building?.id}
            />
          ) : (
            <Text disabled isSecondary qaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}>
              {t(noDataTextKey)}
            </Text>
          )}
        </EntityProfileHeaderInfoItemBase>
      )

    case RequestProfileHeaderInfoItemName.Flat:
      return (
        <EntityProfileHeaderInfoItemBase
          {...restProps}
          {...itemBaseProps}
          contentExist={!!requestData?.flat?.id}
          title={REQUEST_PROFILE_HEADER_INFO_ITEM_TITLE_KEYS[name]}
          value={requestData?.flat?.id}
        >
          {requestData?.flat ? (
            <EntityProfileFlatItem
              blocked={isWorker || isConnectedRequest}
              className={styles.flatItem}
              hoverClassName={null}
              isArchived={requestData?.flat?.archived}
              text={flatText}
              textQaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}
              value={requestData?.flat?.id}
            />
          ) : (
            <Text disabled isSecondary qaName={QA_NAME_PROFILE_INFO_ITEM_VALUE}>
              {t(noDataTextKey)}
            </Text>
          )}
        </EntityProfileHeaderInfoItemBase>
      )

    default:
      return null
  }
}

export default RequestProfileHeaderInfoItem
